import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { DefaultText, PaperBlock } from '@guryou/html-components';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '15px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

// `dropzoneStyle` is an optional style override for the dashed drop target itself
// (merged on top of `baseStyle`, under the drag-state overrides so those still win) —
// existing callers that don't pass it keep the exact original size/look; added so
// FileForm.js (newDesigns/videos) can size the drop target to fill its Card
// instead of forking this component.
// `accept` is likewise an optional override of the default (image + pdf + video + audio) mime
// list, so a caller that only ever wants images — EmailTemplateForm.js's logo upload — can
// restrict the picker/drag-drop instead of forking this component for one narrower mime set.
const ReactDropzoneCSV = ({
  onChange,
  dropzoneStyle,
  accept = 'image/jpg, image/jpeg, image/png, image/webp, image/gif, image/svg+xml, application/pdf, video/*, audio/*',
  ...rest
}) => {
  const { t } = useTranslation();

  const onDrop = useCallback(
    acceptedFiles => {
      onChange && onChange(acceptedFiles);
    },
    [onChange]
  );

  const sizePerTypeValidator = file => {
    const type = file.type.split('/')[0];
    if (type === 'image') {
      if (file.size > 5242880) {
        return {
          code: 'size-too-large',
          message: `image is larger than 5MB`,
        };
      }
    }
    if (type === 'video') {
      if (file.size > 1073741824) {
        return {
          code: 'size-too-large',
          message: `video is larger than 1GB`,
        };
      }
    }
    return null;
  };

  const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 1073741824, //1gb
    accept,
    validator: sizePerTypeValidator,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...dropzoneStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [dropzoneStyle, isDragActive, isDragReject, isDragAccept]
  );

  const files = acceptedFiles.map(file => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
      <ul>
        {errors.map(e => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ));

  return (
    <PaperBlock display="flex" flexDirection="column" alignItems="center" {...rest}>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <DefaultText>{t('PRODUCT.dragAndDropInfo')}</DefaultText>
      </div>
      {!!files.length && (
        <PaperBlock padding={1} marginTop={1}>
          {files}
        </PaperBlock>
      )}

      {!!fileRejectionItems.length && (
        <PaperBlock padding={1} marginTop={1} color="red">
          {fileRejectionItems}
        </PaperBlock>
      )}
    </PaperBlock>
  );
};

export default ReactDropzoneCSV;
